import { CANARY_MISSION } from '@/enum/enum'

export function resolveArthoContentUrl(contentUrl: string): string | null {
  const trimmed = contentUrl.trim()
  if (!trimmed) return null
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return trimmed
  const base = process.env.NEXT_PUBLIC_SITE_URL ?? CANARY_MISSION.BASIC_URL
  try {
    return new URL(trimmed.startsWith('/') ? trimmed : `/${trimmed}`, base).toString()
  } catch {
    return null
  }
}

export function extractBodyInnerHtml(fullOrFragment: string): string {
  const bodyMatch = fullOrFragment.match(/<body[^>]*>([\s\S]*?)<\/body>/i)
  if (bodyMatch) return bodyMatch[1].trim()
  return fullOrFragment.trim()
}

function resolveHrefAgainstBase(href: string, documentUrl: string): string {
  try {
    return new URL(href, documentUrl).toString()
  } catch {
    return href
  }
}

export function extractHeadStylesForEmbed(fullHtml: string, documentUrl: string): string {
  const headMatch = fullHtml.match(/<head[^>]*>([\s\S]*?)<\/head>/i)
  if (!headMatch) return ''
  const headInner = headMatch[1]
  const chunks: string[] = []

  const linkRe = /<link\b[^>]*>/gi
  let linkMatch: RegExpExecArray | null
  while ((linkMatch = linkRe.exec(headInner)) !== null) {
    let tag = linkMatch[0]
    if (!/\brel\s*=\s*["']stylesheet["']/i.test(tag)) continue
    const hrefMatch = tag.match(/\bhref\s*=\s*(["'])([^"']*)\1/i)
    if (hrefMatch) {
      const quote = hrefMatch[1]
      const href = hrefMatch[2]
      const absolute = resolveHrefAgainstBase(href, documentUrl)
      tag = tag.replace(hrefMatch[0], `href=${quote}${absolute}${quote}`)
    }
    chunks.push(tag)
  }

  const styleRe = /<style\b[^>]*>[\s\S]*?<\/style>/gi
  let styleMatch: RegExpExecArray | null
  while ((styleMatch = styleRe.exec(headInner)) !== null) {
    chunks.push(styleMatch[0])
  }

  return chunks.join('\n')
}

export function extractArthoEmbedHtml(fullHtml: string, documentUrl: string): string {
  const head = extractHeadStylesForEmbed(fullHtml, documentUrl)
  const body = extractBodyInnerHtml(fullHtml)
  if (!head) return body
  return `${head}\n${body}`
}

function getFetchCacheOptions(): Pick<RequestInit, 'cache' | 'next'> {
  if (process.env.STATIC_BUILD === 'true') {
    return { cache: 'no-store' }
  }
  return { next: { revalidate: 3600 } }
}

export async function fetchHtmlFromUrl(url: string): Promise<string | null> {
  try {
    const res = await fetch(url, {
      headers: { Accept: 'text/html,application/xhtml+xml;q=0.9,*/*;q=0.8' },
      ...getFetchCacheOptions(),
    })
    const text = await res.text()
    const ct = res.headers.get('content-type') ?? ''  
    if (!res.ok) return null

    if (
      ct.includes('text/html') ||
      ct.includes('application/xhtml') ||
      text.trimStart().startsWith('<')
    ) {
      return text
    }
    return null
  } catch (error) {
    console.error('fetchHtmlFromUrl error', url, error)
    return null
  }
}

export async function fetchJsonFromUrl<T = unknown>(url: string): Promise<T | null> {
  try {
    const res = await fetch(url, {
      headers: { Accept: 'application/json, */*;q=0.1' },
      ...getFetchCacheOptions(),
    })
    if (!res.ok) return null
    const ct = res.headers.get('content-type') ?? ''
    if (!ct.includes('application/json') && !ct.includes('+json')) return null
    return (await res.json()) as T
  } catch {
    return null
  }
}